import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, ChevronDown, ChevronUp, Image as ImageIcon, Info, Trash2 } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';
import MetadataImagem from './MetadataImagem';

const API_ORIGIN = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1').replace(/\/api\/v1$/, '');

// Mesmo catálogo fixo do backend (ver FUNDO_CORES_VALIDAS em
// adminEnqueteController.js) — sem módulo compartilhado entre os dois
// pacotes nesse projeto, mantido em sincronia manualmente. Além dos tons
// neutros clássicos, cobre bege com leve tendência de cada cor primária/
// secundária (vermelho, laranja, amarelo, verde, azul, roxo) — sempre
// dessaturado o bastante pra continuar lendo como "bege".
const FUNDO_CORES = [
  // Neutro clássico
  '#FFFDD0', '#F5F5DC', '#FAF0E6', '#EDE0D4', '#D8CFC4', '#C9C0A8', '#B8A78F', '#8C8577',
  // Vermelho-bege (rosa poeirento/marsala)
  '#F2E1DC', '#E8CFC7', '#DCA1A1', '#C79A93', '#A8817C',
  // Laranja-bege (terracota/pêssego/camelo)
  '#F5DCC4', '#EAC3A0', '#D2A679', '#C28B7A', '#A9694F',
  // Amarelo-bege (dourado/mostarda)
  '#F0E4C2', '#E3C88F', '#C9A96B', '#A68A4A',
  // Verde-bege (sage/musgo/oliva)
  '#E8E8D0', '#D4D3B5', '#A3B18A', '#8C9273', '#6E7358',
  // Azul-bege (azul poeirento/ardósia)
  '#E3E6E1', '#C7CDC9', '#A9B4B8', '#8AA0B0', '#647A8A',
  // Roxo-bege (malva/lavanda acastanhada)
  '#E5DCE0', '#C6B6C2', '#9C8998'
];

// Só a primeira linha (os neutros clássicos) fica visível de cara — o
// resto entra num acordeon, sem peso visual pra quem só quer usar uma
// imagem no fundo (o espaço abaixo fica melhor aproveitado nesse caso).
const PRIMEIRA_LINHA = 7;

// Aba "Personalizar Fundo" do modal Configurar — cor (uma das 24 do
// catálogo) ou imagem (opacidade baixa fixa, só pra dar textura sem
// atrapalhar a leitura das perguntas) pro fundo da página pública da
// enquete. Mutuamente exclusivos: escolher um sempre substitui o outro
// (reforçado no backend, não só aqui). `enquete` já vem carregado do
// ConfigurarEnqueteForm.js — onChange recarrega ele depois de qualquer ação.
const PersonalizarFundoForm = ({ enqueteId, fundoTipo, fundo, metadataFundo, onChange }) => {
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [aplicandoCor, setAplicandoCor] = useState(null);
  // Começa aberto se a cor já ativa está fora da primeira linha — senão a
  // seleção atual ficaria escondida no acordeon fechado ao reabrir o modal.
  const [mostrarTodas, setMostrarTodas] = useState(
    () => fundoTipo === 'cor' && !!fundo && !FUNDO_CORES.slice(0, PRIMEIRA_LINHA).includes(fundo)
  );

  const escolherCor = async (cor) => {
    setAplicandoCor(cor);
    try {
      await adminEnquetesAPI.atualizarFundoCor(enqueteId, cor);
      sucessoComHorario('Cor de fundo aplicada');
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao aplicar cor de fundo');
    } finally {
      setAplicandoCor(null);
    }
  };

  const selecionarArquivo = () => inputRef.current?.click();

  const onArquivoSelecionado = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    setEnviando(true);
    try {
      await adminEnquetesAPI.enviarFundoImagem(enqueteId, arquivo);
      sucessoComHorario('Imagem de fundo enviada');
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao enviar imagem de fundo');
    } finally {
      setEnviando(false);
      e.target.value = '';
    }
  };

  const remover = async () => {
    try {
      await adminEnquetesAPI.removerFundo(enqueteId);
      sucessoComHorario('Fundo personalizado removido');
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao remover fundo');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <p>Aparece por trás das perguntas na página pública da enquete. Cor ou imagem — nunca os dois juntos, e a imagem sempre entra com opacidade bem baixa (fixa, não ajustável) pra não atrapalhar a leitura.</p>
      </div>

      {fundoTipo !== 'nenhum' && (
        <button
          onClick={remover}
          className="flex items-center gap-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg px-3 py-2"
        >
          <Trash2 className="w-3.5 h-3.5" /> Remover personalização de fundo
        </button>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">Cor (35 tons de bege)</p>
        <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
          {(mostrarTodas ? FUNDO_CORES : FUNDO_CORES.slice(0, PRIMEIRA_LINHA)).map((cor) => {
            const ativa = fundoTipo === 'cor' && fundo === cor;
            return (
              <button
                key={cor}
                type="button"
                onClick={() => escolherCor(cor)}
                disabled={aplicandoCor === cor}
                title={cor}
                aria-label={`Usar ${cor} como fundo`}
                className={`relative w-full aspect-square rounded-lg border transition-all ${
                  ativa ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ backgroundColor: cor }}
              >
                {ativa && (
                  // Branco + sombra escura (não a cor do tema) de propósito:
                  // a paleta agora vai do creme quase branco ao taupe bem
                  // escuro, então o check precisa ler em qualquer um dos
                  // dois extremos — uma cor fixa só funcionaria numa ponta.
                  <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]" />
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setMostrarTodas((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 mt-2.5"
        >
          {mostrarTodas ? (
            <>Mostrar menos <ChevronUp className="w-3.5 h-3.5" /></>
          ) : (
            <>Ver mais {FUNDO_CORES.length - PRIMEIRA_LINHA} cores <ChevronDown className="w-3.5 h-3.5" /></>
          )}
        </button>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">Ou uma imagem</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onArquivoSelecionado}
        />

        {fundoTipo === 'imagem' && fundo ? (
          <div className="space-y-3">
            <div>
              <div className="relative h-32 w-full rounded-lg border border-gray-200 overflow-hidden bg-white">
                <img src={`${API_ORIGIN}${fundo}`} alt="Fundo personalizado" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-white/85 flex items-center justify-center text-xs text-gray-500">
                  Prévia (a opacidade real na enquete é bem mais baixa)
                </div>
              </div>
              <MetadataImagem metadata={metadataFundo} />
            </div>
            <button
              type="button"
              onClick={selecionarArquivo}
              disabled={enviando}
              className="text-sm text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-2 border border-gray-200 flex items-center gap-1.5"
            >
              <ImageIcon className="w-3.5 h-3.5" /> {enviando ? 'Enviando...' : 'Trocar imagem'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={selecionarArquivo}
            disabled={enviando}
            className="w-full h-24 text-sm text-gray-500 hover:text-primary-600 hover:border-primary-300 flex flex-col items-center justify-center gap-2 border border-dashed border-gray-300 rounded-lg transition-colors"
          >
            <ImageIcon className="w-5 h-5" /> {enviando ? 'Enviando...' : 'Adicionar imagem de fundo'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonalizarFundoForm;
